import React, { FocusEventHandler, JSX, useState } from 'react';
import EDIconButton from '../IconButton/EDIconButton';
import EDLabelMenu from '../../LocalMenu/EDLabelMenu';
import { styled } from '@mui/material';
import { CommonPropsAndTypes } from '../../_Types/StandardComponentProps';
import { IconType } from '../../Icon/Utils/IconDefinitions';
import EDButton from '../Button/EDButton';
import EDIcon from '../../Icon/EDIcon';
import { spacing, SpacingProps } from '@mui/system';
import { PopoverOrigin } from '@mui/material/Popover';

interface IProps extends SpacingProps {
  icon: IconType | JSX.Element;
  buttonLabel?: string;
  menuLabel?: string;
  tooltip: string;
  iconColor?: CommonPropsAndTypes.ColorType;
  color?: CommonPropsAndTypes.ColorType;
  primary?: boolean;
  iconSize?: CommonPropsAndTypes.StandardSizesType | 'default' | 'extraLarge';
  noClickPropagation?: boolean;
  highEmphasis?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  buttonRef?: React.MutableRefObject<HTMLButtonElement | null>;
  captionLabel?: boolean;
  noRightMargin?: boolean;
  onlyOnHover?: boolean;
  testId?: string;
  hide?: boolean;
  position?: React.CSSProperties['position'];
  positionRight?: boolean;
  buttonHighEmphasis?: boolean;
  transparentBackground?: boolean;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  disableTabbing?: boolean;
  fullwidth?: boolean;
  onFocus?: FocusEventHandler<HTMLButtonElement>;
  /** Custom render function for the trigger button. When provided, takes priority over built-in button/icon rendering. */
  renderButton?: (props: {
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
    disabled?: boolean;
    icon: IconType | JSX.Element;
    label: string;
  }) => React.ReactElement<any>;
}

export const EDMenuButtonBase: React.FC<
  React.PropsWithChildren<IProps & { className?: string }>
> = props => {
  const [menuOpen, setMenuOpen] = useState(false);
  const onClick = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    eventOnClick: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void
  ) => {
    if (props.noClickPropagation) {
      event.stopPropagation();
    }
    eventOnClick(event);
  };

  return (
    <>
      <EDLabelMenu
        menuLabel={props.menuLabel}
        clickableElement={eventOnClick => {
          if (props.renderButton) {
            return props.renderButton({
              onClick: event => onClick(event, eventOnClick),
              disabled: props.disabled,
              icon: props.icon,
              label: props.buttonLabel ?? props.tooltip,
            });
          }
          if (!props.buttonLabel) {
            return (
              <EDIconButton
                onClick={event => onClick(event, eventOnClick)}
                color={props.iconColor}
                icon={props.icon}
                iconSize={props.iconSize ?? 'medium'}
                margin={props.margin ?? 0.5}
                padding={props.padding ?? 0.75}
                marginBottom={props.marginBottom}
                marginLeft={props.marginLeft}
                marginRight={props.marginRight}
                marginTop={props.marginTop}
                marginX={props.marginX}
                marginY={props.marginY}
                disableTabbing={props.disableTabbing}
                onFocus={props.onFocus}
                sx={{
                  ...(props.transparentBackground && {
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                  }),
                  ...(props.buttonHighEmphasis && {
                    padding: 0.75,
                    backgroundColor: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  }),

                  ...(props.positionRight && {
                    position: 'relative',
                    right: 9,
                  }),
                  ...(props.position && {
                    position: props.position,
                  }),
                  ...(props.hide && {
                    opacity: 0,
                  }),
                  ...(menuOpen && { opacity: 1 }),
                  ...(props.highEmphasis && {
                    backgroundColor: 'primary.main',
                    boxShadow: 1,
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  }),
                  ...(!menuOpen &&
                    props.onlyOnHover && {
                      opacity: 0,
                      transition: 'opacity 100ms ease',
                      '.MuiCardActionArea-root:hover &': {
                        opacity: 1,
                      },
                      '.MuiListItem-root:hover &': {
                        opacity: 1,
                      },
                      '.MuiDataGrid-row:hover &': {
                        opacity: 1,
                      },
                    }),
                }}
                ref={props.buttonRef}
                tooltip={props.tooltip}
                disabled={props.disabled}
                disabledReason={props.disabledReason}
                testId={props.testId}
              />
            );
          } else {
            return (
              <EDButton
                icon={
                  typeof props.icon === 'string' ? (
                    <EDIcon
                      marginRight={!props.noRightMargin ? 1 : undefined}
                      color={props.iconColor}
                      iconType={props.icon as IconType}
                    />
                  ) : (
                    props.icon
                  )
                }
                margin={props.margin}
                marginBottom={props.marginBottom}
                marginLeft={props.marginLeft}
                marginRight={props.marginRight}
                marginTop={props.marginTop}
                marginX={props.marginX}
                marginY={props.marginY}
                label={props.buttonLabel}
                buttonRef={props.buttonRef}
                labelNotUpperCase
                lowEmphasis
                flat
                primary={props.primary}
                color={props.color}
                padding={props.padding ?? 1}
                captionLabel={props.captionLabel}
                onClick={event => onClick(event, eventOnClick)}
                disabled={props.disabled}
                disabledReason={props.disabledReason}
                testId={props.testId}
                fullWidth={props.fullwidth}
                alignLeft
              />
            );
          }
        }}
        noClickPropagation={props.noClickPropagation}
        setMenuOpen={setMenuOpen}
        anchorOrigin={props.anchorOrigin}
        transformOrigin={props.transformOrigin}
      >
        {props.children}
      </EDLabelMenu>
    </>
  );
};
const EDMenuButton: React.FC<React.PropsWithChildren<IProps>> = styled(
  EDMenuButtonBase,
  {
    name: 'EDMenuButton',
  }
)(spacing);
export default EDMenuButton;
